class NostoCollection {
    constructor(productListSelector) {
        this.productList = document.querySelector(productListSelector);
        if (!this.productList) {
            this.removeSkeletons();
            console.error('Product list element not found');
            return;
        }
        this.collectionId = this.productList.parentElement.dataset.collectionId;
        this.currentPage = 0;
        this.productsPerPage = parseInt(this.productList.parentElement.dataset.productsPerPage, 10) || 16;
        this.api = null;

        this.init();
    }

    init() {
        window.onload = async () => {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const hasFilter = Array.from(urlParams.keys()).some(key => key.startsWith('filter'));
    
                if (hasFilter) {
                    this.showFallbackContent();
                } else {
                    await this.initializeNosto();
                    this.setupPagination();
                    this.setupFilter();
                    this.setupSorting();
                }
            } catch (error) {
                console.error('Error initializing Nosto collection:', error);
            }
        };
    }

    removeSkeletons() {
        document.querySelectorAll('.skeleton').forEach(skeleton => skeleton.remove());
    }

    updateURLParameter(key, value) {
        const url = new URL(window.location);
        url.searchParams.set(key, value);
        window.history.pushState({}, '', url);
    }

    getURLParameter(name) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    }

    async checkElement(selector) {
        while (document.querySelector(selector) === null) {
            await new Promise(resolve => requestAnimationFrame(resolve))
        }
        return document.querySelector(selector);
    }

    getCachedData(key) {
        const cachedData = sessionStorage.getItem(key);
        if (!cachedData) {
            return null;
        }
        const { data, timestamp } = JSON.parse(cachedData);
        const now = new Date().getTime();
        // set 1 hour caching for performance purposes
        if (now - timestamp > 3600000) {
            sessionStorage.removeItem(key);
            return null;
        }
        return data;
    }

    setCachedData(key, data) {
        const timestamp = new Date().getTime();
        sessionStorage.setItem(key, JSON.stringify({ data, timestamp }));
    }

    async fetchUrls(size, from) {
        try {
            const response = await this.api.search({
                products: {
                    categoryId: this.collectionId,
                    fields: ['url'],
                    size: size,
                    from: from
                }
            }, {
                track: 'category',
                redirect: true
            });

            const result = response || [];

            if (!result.products) {
                throw new Error('No products returned from Nosto API');
            }

            const urls = result.products?.hits?.map(product => product.url) || [];
            const handles = urls.map(url => {
                const parts = url.split('/');
                return parts[parts.length - 1];
            });

            const totalResults = result.products.total || 0;
            const totalPages = Math.ceil(totalResults / size);
            const currentPage = Math.floor(from / size) + 1;

            return {
                handles: handles.join(','),
                currentPage: currentPage,
                totalPages: totalPages,
                totalResults: totalResults,
            };
        } catch (error) {
            this.showFallbackContent();
            console.error('Error fetching URLs from Nosto API:', error);
            return {
                handles: '',
                currentPage: 0,
                totalPages: 0,
                totalResults: 0,
            };
        }
    }

    async loadProducts(page) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
        const cacheKey = `nostoProductHandlesPage${page}_Collection${this.collectionId}_PerPage${this.productsPerPage}`;
        let handlesParameter = this.getCachedData(cacheKey);
    
        let paginationData;
        if (!handlesParameter) {
            try {
                paginationData = await this.fetchUrls(this.productsPerPage, (page - 1) * this.productsPerPage);
                handlesParameter = paginationData.handles;
                this.totalPages = paginationData.totalPages;
    
                if (!handlesParameter) {
                    console.error('Nosto: No handles to fetch');
                    this.showFallbackContent();
                    return false;
                }
    
                this.setCachedData(cacheKey, handlesParameter);
            } catch (error) {
                console.error('Error fetching URLs:', error);
                return false;
            }
        } else {
            paginationData = await this.fetchUrls(this.productsPerPage, (page - 1) * this.productsPerPage);
            this.totalPages = paginationData.totalPages;
            if(handlesParameter !== paginationData.handles){
                handlesParameter = paginationData.handles;
            }
        }
    
        const paginationHtml = this.renderPagination(page, this.totalPages);
        document.querySelector('#pagination-nosto').innerHTML = paginationHtml;
    
        try {
            const urls = handlesParameter;
            const resultArray = urls.split(',');
            function isEmptyProductCard(cardHtml) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(cardHtml?.["product-card"], 'text/html');
                const productCard = doc.querySelector('product-card');
                if (!productCard) return true; 
                const dataProductId = productCard.getAttribute('data-product-id');
                return !dataProductId;
              }
              
            if (resultArray[0] !== "") {
                const promises = resultArray.map((url) => fetch(`${window.Shopify.routes.root}products/${url}?sections=product-card`).then(res => res.json()));
    
                Promise.all(promises).then((res) => {
                    // Filtrer le tableau pour supprimer les produits vides
                    const filteredProductCards = res.filter(
                      (product) => !isEmptyProductCard(product)
                    );

                    const combinedHTML = filteredProductCards.map(r => r['product-card'].replace(/^<div.*?class="shopify-section[^>]*?>/, "").replace(/<\/div>$/, "")).join("");
                    this.productList.closest("#main").classList.add('nosto-loaded');
                    this.productList.innerHTML = combinedHTML;
                    this.productList.style.opacity = 1;
    
                    this.checkElement('product-card[reveal-on-scroll="true"]').then(() => {
                        const productCards = this.productList.querySelectorAll('product-card[reveal-on-scroll="true"]');
                        if (productCards)
                            productCards.forEach(card => card.setAttribute('is-nosto-product', ''));
                        this.revealCards(productCards);       
                                                
                        productCards.forEach(card => {
                         const anchor =  card.querySelector('a[data-instant]');
                         const url = window.location.origin + anchor.getAttribute('href');
                         card.addEventListener('click', (event) => {
                            setTimeout( () => {
                                nostojs(api => {
                                    api.recordSearchClick('category', {
                                        productId: card.dataset.productId,
                                        url: url,
                                    });
                                });
                            },200)
                         });
                        });

                    });
                });
    
                document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
                return true;

            } else {
                console.error('Error: Invalid data structure', data);
                return false;
            }
          
        } catch (error) {
            console.error('Error during section render fetch:', error);
            return false;
        }
    }

    revealCards(elements) {
        elements.forEach((element, index) => {
            setTimeout(() => {
               if (index === 0) {
                  document.dispatchEvent(new Event('collection-rendred'));
                }
                element.classList.add('visible');
            }, index * 50 + 400);
        });
      }

    setupFilter() {
        document.addEventListener("facet:update", () => {
            if (!location.href.includes('?')) {
                document.querySelector('product-list').innerHTML = "";
                history.pushState({}, null, location.href.split('?')[0]);
                window.location.href = location.href;
            }
        });
    }

    setupSorting() {
        localStorage.setItem("clear_sort", false);
        if (localStorage.getItem("clear_sort") === "false") {
            const sortOptions = document.querySelectorAll('.popover-listbox__option group');
            if (!sortOptions)
                return;
            sortOptions?.forEach(function (opt) {
                if (opt.checked) opt.removeAttribute('checked');
            });
            if (location.href.includes('?sort_by')) {
                history.pushState({}, null, location.href.split('?')[0]);
            }
            localStorage.setItem("clear_sort", true);
        }
    }

    setupPagination() {
        let paginationContainer = document.querySelector('#pagination-nosto');
        if (!paginationContainer) {
            paginationContainer = document.createElement('div');
            paginationContainer.id = 'pagination-nosto';
            this.productList.parentElement.appendChild(paginationContainer);
        }
    
        this.currentPage = this.getURLParameter('page') ? parseInt(this.getURLParameter('page'), 10) : 1;
        this.currentPage = isNaN(this.currentPage) || this.currentPage < 1 ? 1 : this.currentPage;
    
        const totalPages = this.totalPages || 1;
    
        this.renderPagination(this.currentPage, totalPages);
    
        paginationContainer.addEventListener('click', async (event) => {
            if (event.target.tagName === 'A') {
                event.preventDefault();
    
                const page = parseInt(event.target.getAttribute('data-page'), 10);
                if (page && page !== this.currentPage) {
                    this.currentPage = page;
                    this.productList.style.opacity = 0.4;
    
                    const success = await this.loadProducts(this.currentPage);
                    if (success) {
                        this.updateURLParameter('page', this.currentPage);
                        this.renderPagination(this.currentPage, totalPages);
                        document.querySelector(".collection").scrollIntoView({ block: "start", behavior: "smooth" });
                    }
                }
            }
        });
    }
    
    renderPagination(currentPage, totalPages) {
        let paginationHtml = '';
    
        if (totalPages <= 1) return paginationHtml; 

        const rightArrowSvg = `
        <svg aria-hidden="true" focusable="false" fill="none" width="11" class="icon icon-chevron-right  icon--direction-aware" viewBox="0 0 10 10">
            <path d="m3 9 4-4-4-4" stroke="currentColor" stroke-linecap="square"></path>
        </svg>`;

         const leftArrowSvg = `
        <svg aria-hidden="true" focusable="false" fill="none" width="11" class="icon icon-chevron-left  icon--direction-aware" viewBox="0 0 10 10">
            <path d="m7 9-4-4 4-4" stroke="currentColor" stroke-linecap="square"></path>
        </svg>`;
    
        if (currentPage > 1) {
            paginationHtml += `<a href="#" data-page="${currentPage - 1}" class="pagination__link h6 pagination-prev">${leftArrowSvg}</a>`;
        }
    
        const range = 2;
        let startPage, endPage;
    
        if (totalPages <= 2 * range + 1) {
            startPage = 1;
            endPage = totalPages;
        } else {
            if (currentPage <= range) {
                startPage = 1;
                endPage = range * 2 + 1;
            } else if (currentPage + range >= totalPages) {
                startPage = totalPages - range * 2;
                endPage = totalPages;
            } else {
                startPage = currentPage - range;
                endPage = currentPage + range;
            }
        }
    
        if (startPage > 1) {
            paginationHtml += `<a href="#" data-page="1" class="pagination__link h6 pagination-page">1</a>`;
            if (startPage > 2) paginationHtml += `<a href="#" class="pagination__link h6 pagination__ellipsis">...</a>`;
        }
    
        for (let i = startPage; i <= endPage; i++) {
            paginationHtml += `<a href="#" data-page="${i}" ${i === currentPage ? 'aria-current="page"' : ''} class="pagination__link h6 pagination-page ${i === currentPage ? 'pagination__link--disabled' : ''}">${i}</a>`;
        }
    
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) paginationHtml += `<a href="#" class="pagination__link h6 pagination__ellipsis">...</a>`;
            paginationHtml += `<a href="#" data-page="${totalPages}" class="pagination__link h6 pagination-page">${totalPages}</a>`;
        }
    
        if (currentPage < totalPages) {
            paginationHtml += `<a href="#" data-page="${currentPage + 1}" class="pagination__link h6 pagination-next">${rightArrowSvg}</a>`;
        }
    
        return paginationHtml;
    }
    
    

    async initializeNosto() {
        try {
            nostojs(async nostoApi => {
                console.info('Nosto API initialized');
                this.api = nostoApi;
                this.api.loadRecommendations();

                const pageParam = this.getURLParameter('page');
                if (pageParam) {
                    this.currentPage = parseInt(pageParam, 10);
                } else {
                    this.currentPage = isNaN(this.currentPage) || this.currentPage < 1 ? 1 : this.currentPage;
                }

                const success = await this.loadProducts(this.currentPage);
                if (!success) {
                    this.currentPage = 1;
                    await this.loadProducts(this.currentPage);
                }
            });
        } catch (error) {
            console.error('Error initializing Nosto API:', error);
            this.showFallbackContent();
            return {
                handles: '',
                currentPage: 0,
                totalPages: 0,
                totalResults: 0,
            };

        }
    }


showFallbackContent() {
    this.removeSkeletons();
    this.productList.closest("#main").classList.add('nosto-fallback');
    this.productList.style.opacity = 1;
}

}

new NostoCollection('product-list[is-nosto-collection]');
